import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { Skeleton } from '@/components/ui/skeleton';
import { Calendar, Clock, Eye, Search, Filter } from 'lucide-react';
import MetaTags from '@/components/MetaTags';
import Breadcrumbs from '@/components/Breadcrumbs';
import { apiUrl } from '@/lib/apiBase';
interface BlogPost {
    id: number;
    title: string;
    slug: string;
    excerpt: string;
    status: string;
    featured_image?: string;
    views: number;
    author: string;
    created_at: string;
    updated_at: string;
    published_at?: string;
    category_name?: string;
    category_slug?: string;
}
interface Category {
    id: number;
    name: string;
    slug: string;
    description?: string;
    post_count: number;
}
const Blog: React.FC = () => {
    const [posts, setPosts] = useState<BlogPost[]>([]);
    const [categories, setCategories] = useState<Category[]>([]);
    const [loading, setLoading] = useState(true);
    const [searchTerm, setSearchTerm] = useState('');
    const [selectedCategory, setSelectedCategory] = useState('');
    const [currentPage, setCurrentPage] = useState(1);
    const [totalPages, setTotalPages] = useState(1);
    const [totalPosts, setTotalPosts] = useState(0);
    useEffect(() => {
        fetchPosts();
        fetchCategories();
    }, [currentPage, selectedCategory, searchTerm]);
    const fetchPosts = async () => {
        try {
            setLoading(true);
            const params = new URLSearchParams({
                action: 'list',
                status: 'published',
                page: currentPage.toString(),
                limit: '9'
            });
            if (selectedCategory) {
                params.append('category', selectedCategory);
            }
            if (searchTerm) {
                params.append('search', searchTerm);
            }
            const response = await fetch(`${apiUrl('/api/blog.php')}?${params}`);
            const data = await response.json();
            if (data.posts) {
                setPosts(data.posts);
                setTotalPages(data.pagination.total_pages);
                setTotalPosts(data.pagination.total_posts);
            }
        }
        catch (error) {
            console.error('خطا در دریافت مقالات:', error);
        }
        finally {
            setLoading(false);
        }
    };
    const fetchCategories = async () => {
        try {
            const response = await fetch(`${apiUrl('/api/blog.php')}?action=categories`);
            const data = await response.json();
            if (data.categories) {
                setCategories(data.categories);
            }
        }
        catch (error) {
            console.error('خطا در دریافت دسته‌بندی‌ها:', error);
        }
    };
    const handleSearch = (e: React.FormEvent) => {
        e.preventDefault();
        setCurrentPage(1);
        fetchPosts();
    };
    const handleCategoryChange = (categorySlug: string) => {
        setSelectedCategory(categorySlug);
        setCurrentPage(1);
    };
    const formatDate = (dateString: string) => {
        return new Date(dateString).toLocaleDateString('fa-IR');
    };
    const breadcrumbItems = [
        { label: 'خانه', path: '/' },
        { label: 'بلاگ', path: '/blog' }
    ];
    return (<div className="min-h-screen bg-gray-50">
      <MetaTags title="بلاگ - آزمایشگاه تشخیص پزشکی سلامت" description="مقالات و مطالب مفید در زمینه سلامت، آزمایشات پزشکی و اخبار آزمایشگاه سلامت" keywords="بلاگ سلامت, مقالات پزشکی, آزمایشات پزشکی, سلامت" canonical="/blog"/>
      
      <div className="container mx-auto px-4 sm:px-6 py-6 sm:py-8">
        <Breadcrumbs items={breadcrumbItems}/>
        
        
        <div className="text-center mb-8 sm:mb-12">
          <h1 className="text-2xl sm:text-3xl md:text-4xl font-bold text-gray-900 mb-4">بلاگ سلامت</h1>
          <p className="text-base sm:text-lg md:text-xl text-gray-600 max-w-3xl mx-auto px-2">
            مقالات و مطالب مفید در زمینه سلامت، آزمایشات پزشکی و اخبار آزمایشگاه سلامت
          </p>
        </div>

        
        <div className="mb-8">
          <form onSubmit={handleSearch} className="flex flex-col md:flex-row gap-4 mb-6">
            <div className="flex-1 relative">
              <Search className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5"/>
              <Input type="text" placeholder="جستجو در مقالات..." value={searchTerm} onChange={(e) => setSearchTerm(e.target.value)} className="pr-10"/>
            </div>
            <Button type="submit" className="md:w-auto">
              جستجو
            </Button>
          </form>

          
          <div className="flex flex-wrap gap-2">
            <Button variant={selectedCategory === '' ? 'default' : 'outline'} size="sm" onClick={() => handleCategoryChange('')}>
              همه مقالات
            </Button>
            {categories.map((category) => (<Button key={category.id} variant={selectedCategory === category.slug ? 'default' : 'outline'} size="sm" onClick={() => handleCategoryChange(category.slug)}>
                {category.name} ({category.post_count})
              </Button>))}
          </div>
        </div>

        
        {loading ? (<div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {Array.from({ length: 6 }).map((_, i) => (<Card key={i}>
                <Skeleton className="h-48 w-full"/>
                <CardHeader>
                  <Skeleton className="h-6 w-3/4"/>
                  <Skeleton className="h-4 w-1/2"/>
                </CardHeader>
                <CardContent>
                  <Skeleton className="h-4 w-full mb-2"/>
                  <Skeleton className="h-4 w-2/3"/>
                </CardContent>
              </Card>))}
          </div>) : posts.length > 0 ? (<>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 mb-8">
              {posts.map((post) => (<Card key={post.id} className="hover:shadow-lg transition-shadow duration-300 overflow-hidden rounded-2xl">
                  {post.featured_image && (<div className="aspect-video overflow-hidden rounded-t-2xl">
                      <img src={post.featured_image} alt={post.title} className="w-full h-full object-cover hover:scale-105 transition-transform duration-300"/>
                    </div>)}
                  <CardHeader>
                    <div className="flex items-center gap-2 mb-2">
                      {post.category_name && (<Badge variant="secondary">{post.category_name}</Badge>)}
                    </div>
                    <CardTitle className="line-clamp-2">
                      <Link to={`/blog/${post.slug}`} className="hover:text-blue-600 transition-colors">
                        {post.title}
                      </Link>
                    </CardTitle>
                    <CardDescription className="line-clamp-3">
                      {post.excerpt}
                    </CardDescription>
                  </CardHeader>
                  <CardContent>
                    <div className="flex items-center justify-between text-sm text-gray-500">
                      <div className="flex items-center gap-4">
                        <div className="flex items-center gap-1">
                          <Calendar className="w-4 h-4"/>
                          {formatDate(post.published_at || post.created_at)}
                        </div>
                        <div className="flex items-center gap-1">
                          <Eye className="w-4 h-4"/>
                          {post.views}
                        </div>
                      </div>
                      <span className="text-xs">{post.author}</span>
                    </div>
                  </CardContent>
                </Card>))}
            </div>

            
            {totalPages > 1 && (<div className="flex justify-center items-center gap-2">
                <Button variant="outline" disabled={currentPage === 1} onClick={() => setCurrentPage(currentPage - 1)}>
                  قبلی
                </Button>
                
                <div className="flex gap-1">
                  {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
                    const page = i + 1;
                    return (<Button key={page} variant={currentPage === page ? 'default' : 'outline'} size="sm" onClick={() => setCurrentPage(page)}>
                        {page}
                      </Button>);
                })}
                </div>
                
                <Button variant="outline" disabled={currentPage === totalPages} onClick={() => setCurrentPage(currentPage + 1)}>
                  بعدی
                </Button>
              </div>)}
          </>) : (<div className="text-center py-12">
            <div className="text-gray-500 text-lg mb-4">
              {searchTerm || selectedCategory ? 'مقاله‌ای یافت نشد' : 'هنوز مقاله‌ای منتشر نشده است'}
            </div>
            {(searchTerm || selectedCategory) && (<Button onClick={() => {
                    setSearchTerm('');
                    setSelectedCategory('');
                    setCurrentPage(1);
                }}>
                پاک کردن فیلترها
              </Button>)}
          </div>)}
      </div>
    </div>);
};
export default Blog;
